/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import dayjs from 'dayjs';
import { useRef } from 'react';

import { ConnectivityState, TelemetryActivityRow } from '@/client';
import { LoadMoreSentinel } from '@/hooks/useLoadMoreOnIntersect';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Link } from '@/primitives/Link';

function formatOccurrences(count: number): string {
  if (count >= 100) return '99+';
  return String(count);
}

function formatTimestampUtc(ts: string): string {
  return dayjs(ts).utc().format('D MMM YYYY HH:mm:ss');
}

const CONNECTIVITY_LABELS: Record<string, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  degraded: 'Degraded',
};

function connectivityLabel(state: ConnectivityState | null | undefined): string {
  if (!state) return 'Unknown';
  return CONNECTIVITY_LABELS[state] ?? state.charAt(0).toUpperCase() + state.slice(1);
}

function connectivityClass(state: ConnectivityState | null | undefined): string {
  if (!state) return 'connectivity-dot connectivity-unknown';
  return `connectivity-dot connectivity-${state}`;
}

function ConnectivityCell({ state }: { state: ConnectivityState | null | undefined }) {
  return (
    <span className="connectivity" data-testid="connectivity-cell">
      <span className={connectivityClass(state)} aria-hidden="true" />
      <span className="connectivity-label">{connectivityLabel(state)}</span>
    </span>
  );
}

export type ActivityTableProps = {
  title: string;
  rows: TelemetryActivityRow[];
  tenantId: string;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  isError: boolean;
  errorMessage?: string;
  fetchNextPage: () => void;
};

function ActivityRowView({ row, tenantId }: { row: TelemetryActivityRow; tenantId: string }) {
  return (
    <tr data-testid="activity-row">
      <td className="activity-node">
        <Link to={`/tenants/${tenantId}/nodes/${row.nodeId}`}>{row.nodeName}</Link>
      </td>
      <td>
        <span className="occurrence-pill">{formatOccurrences(row.occurrences)}</span>
      </td>
      <td>
        <ConnectivityCell state={row.nodeConnectivity} />
      </td>
      <td className="activity-ts">{formatTimestampUtc(row.lastSeen)}</td>
    </tr>
  );
}

function ActivityFooter({
  isFetchingNextPage,
  isError,
  errorMessage,
}: {
  isFetchingNextPage: boolean;
  isError: boolean;
  errorMessage?: string;
}) {
  const { t } = useMimicTranslation('activity');
  if (isFetchingNextPage) {
    return <div className="activity-table-footer">{t('loadingMore')}</div>;
  }
  if (isError) {
    return (
      <div className="activity-table-footer activity-error" data-testid="activity-error">
        {errorMessage ?? t('failedToLoad')}
      </div>
    );
  }
  return null;
}

function ActivityTableBody({
  rows,
  tenantId,
  hasNextPage,
  isLoading,
  isFetchingNextPage,
  isError,
  errorMessage,
  fetchNextPage,
}: Omit<ActivityTableProps, 'title'>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const { t } = useMimicTranslation('activity');

  return (
    <div className="activity-table-wrap" ref={wrapRef} data-testid="activity-table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t('table.hostName')}</th>
            <th>{t('table.occurrences')}</th>
            <th>{t('table.connectivity')}</th>
            <th>{t('table.lastSeen')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <ActivityRowView key={row.nodeId} row={row} tenantId={tenantId} />
          ))}
        </tbody>
      </table>
      <ActivityFooter isFetchingNextPage={isFetchingNextPage} isError={isError} errorMessage={errorMessage} />
      <LoadMoreSentinel
        rootRef={wrapRef}
        enabled={hasNextPage && !isLoading && !isFetchingNextPage && !isError}
        onIntersect={fetchNextPage}
      />
    </div>
  );
}

function ActivityTableContent(props: ActivityTableProps) {
  const { title, rows, isLoading, isError, errorMessage } = props;
  const { t } = useMimicTranslation('activity');
  if (isLoading && rows.length === 0) {
    return <div className="activity-empty">{t('loading')}</div>;
  }
  if (rows.length === 0 && isError) {
    return (
      <div className="activity-empty activity-error" data-testid="activity-error">
        {errorMessage ?? t('failedToLoad')}
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="activity-empty">{t('emptyWindow', { section: title.toLowerCase() })}</div>;
  }
  return <ActivityTableBody {...props} />;
}

export function ActivityTable(props: ActivityTableProps) {
  const { title } = props;
  return (
    <section className="activity-card" data-testid={`activity-card-${title.toLowerCase()}`}>
      <h2 className="activity-card-title">{title}</h2>
      <ActivityTableContent {...props} />
    </section>
  );
}
